import { useCallback, useState } from 'react';
import { useServices, useTenantConfig, useGlobalData } from '../contexts';
import { useAuth } from '@teko/auth-react';
import get from 'lodash.get';
import terraHelper from 'helpers/terra';
import {
  PaymentMethod as IPaymentMethod,
  PaymentDeposit as IPaymentDeposit,
  PaymentMethodCode,
  PaymentMethodGroupCode,
} from '@teko/payment';

export interface UsePaymentMethods {
  paymentMethods: IPaymentMethod[];
  resetPaymentMethods: () => void;
  onAddVnpayToken: (cardType: number) => Promise<void>;
  paymentDeposit?: IPaymentDeposit;
  loading: boolean;
}

export const usePaymentMethods = (orderDetail?: any): UsePaymentMethods => {
  const { terminal, channel, baseDomain, isMobile } = useTenantConfig();
  const [loading, setLoading] = useState(true);
  const { cartService, paymentService } = useServices();

  const defaultParamsCart = { terminal, channel };
  const { loading: loadingIAMProfile, user: myProfile } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [paymentDeposit, setPaymentDeposit] = useState<IPaymentDeposit>();
  const { paymentType } = useGlobalData();

  const resetPaymentMethods = useCallback(() => {
    if (
      !loadingIAMProfile &&
      (paymentType === 'normal' ||
        (paymentType === 'terra' && !terraHelper.isAvailable()))
    )
      cartService
        .getPaymentMethod(
          defaultParamsCart,
          orderDetail?.originalCode || orderDetail?.code || null,
        )
        .then(({ paymentMethods: res, merchantCode, deposit }: any) => {
          if (res) {
            const methods = (res as IPaymentMethod[]).map((method) => ({
              ...method,
              merchantCode,
            }));
            // move VNPAY_GATEWAY first
            const idxVnpayGateway = methods.findIndex(
              (el) => el.methodCode === PaymentMethodCode.VNPAY_GATEWAY,
            );
            if (idxVnpayGateway >= 1) {
              const temp = methods[idxVnpayGateway];
              methods[idxVnpayGateway] = methods[0];
              methods[0] = temp;
            }
            const applicableMethods = isMobile
              ? methods.filter(
                  // installment is currently unavailable on mobile
                  (item) =>
                    ![PaymentMethodGroupCode.INSTALLMENT as string].includes(
                      item.methodGroupCode.toUpperCase(),
                    ),
                )
              : methods;
            setPaymentMethods(
              deposit?.allowDeposit
                ? applicableMethods.filter(
                    (method: IPaymentMethod) =>
                      method.methodGroupCode.toUpperCase() !==
                        PaymentMethodGroupCode.CREDIT &&
                      method.methodCode !== PaymentMethodCode.COD &&
                      method.methodCode !== PaymentMethodCode.COD_OFFLINE,
                  )
                : applicableMethods,
            );
            setPaymentDeposit({
              ...deposit,
              method: methods.find(
                (method: IPaymentMethod) =>
                  method.methodCode === PaymentMethodCode.COD,
              ),
            });
          }
        })
        .finally(() => setLoading(false));
  }, [terminal, loadingIAMProfile, JSON.stringify(orderDetail)]);

  const onAddVnpayToken = async (cardType: number): Promise<void> => {
    const vnpayTokenMethods = paymentMethods.filter(
      (el) => el.methodCode === PaymentMethodCode.VNPAY_TOKEN,
    );
    const merchantMethodCode = get(vnpayTokenMethods, '0.merchantMethodCode');
    if (merchantMethodCode) {
      const res = await paymentService.requestCreateToken(
        merchantMethodCode,
        myProfile?.userId,
        cardType,
        `${baseDomain}/checkout`,
        `${baseDomain}/checkout`,
      );
      if (res && res.returnUrl) {
        window.location.href = res.returnUrl;
      }
    }
  };

  return {
    paymentMethods,
    resetPaymentMethods,
    onAddVnpayToken,
    paymentDeposit,
    loading,
  };
};
